import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import type { GraphSnapshot } from "../domain/model";
import { fetchGraph } from "../lib/api";

interface GraphDataState {
  snapshot: GraphSnapshot | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

const GraphDataContext = createContext<GraphDataState | null>(null);

export function GraphDataProvider({ children }: { children: ReactNode }) {
  const [snapshot, setSnapshot] = useState<GraphSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchGraph()
      .then((data) => {
        if (!active) return;
        setSnapshot(data);
        setError(null);
      })
      .catch((reason: unknown) => {
        if (!active) return;
        setError(reason instanceof Error ? reason.message : "Graph API failed.");
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [version]);

  const value = useMemo<GraphDataState>(() => ({
    snapshot, loading, error, reload: () => setVersion((current) => current + 1),
  }), [snapshot, loading, error]);

  return <GraphDataContext.Provider value={value}>{children}</GraphDataContext.Provider>;
}

export function useGraphData(): GraphDataState {
  const context = useContext(GraphDataContext);
  if (!context) throw new Error("useGraphData must be used inside GraphDataProvider.");
  return context;
}
